import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  SignalCellularAlt as SignalIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { monitoringAPI } from '../services/api';
import CallsListTable from './CallsListTable';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { getMOSColorHex, getMOSQualityText } from '../utils/mosUtils';

// MOS color coding - using shared utility
const getMOSColor = getMOSColorHex;

// R-Factor color coding
const getRFactorColor = (rFactor) => {
  if (!rFactor || rFactor === 0) return '#999';
  if (rFactor >= 90) return '#4caf50';  // Excellent
  if (rFactor >= 80) return '#8bc34a';  // Good
  if (rFactor >= 70) return '#ff9800';  // Fair
  if (rFactor >= 60) return '#ff5722';  // Poor
  return '#f44336';  // Unusable
};

// Get R-Factor quality label
const getRFactorLabel = (rFactor) => {
  if (!rFactor || rFactor === 0) return 'N/A';
  if (rFactor >= 90) return 'EXCELLENT';
  if (rFactor >= 80) return 'GOOD';
  if (rFactor >= 70) return 'FAIR';
  if (rFactor >= 60) return 'POOR';
  return 'UNUSABLE';
};

// Agent Detail Modal with Graphs
function AgentDetailModal({ open, onClose, cluster, agentId, hours = 24 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (open && cluster && agentId) {
      fetchAgentDetail();
    }
  }, [open, cluster, agentId, hours]);

  const fetchAgentDetail = async () => {
    setLoading(true);
    try {
      const response = await monitoringAPI.get(`/dashboard/audio-quality/agent/${cluster}/${agentId}?hours=${hours}`);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching agent detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              Agent Audio Quality: {agentId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cluster: {cluster} • Last {hours} hours • {data?.total_calls || 0} calls
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {/* Red Flags Alert */}
            {data?.confidence_score && data.confidence_score.length > 0 && (
              (() => {
                const avgConfidence = data.confidence_score.reduce((sum, d) => sum + d.value, 0) / data.confidence_score.length;
                const avgLoss = data.packet_loss?.reduce((sum, d) => sum + d.value, 0) / (data.packet_loss?.length || 1);
                const avgJitter = data.mean_interval?.reduce((sum, d) => sum + d.value, 0) / (data.mean_interval?.length || 1);
                const hasFlush = data.flush_packet_count?.some(d => d.value > 0);
                const hasBufferStress = data.largest_jb_size?.some(d => d.value > 120);
                
                const redFlags = [];
                if (avgConfidence < 0.10) redFlags.push('Low Confidence (Silence)');
                if (avgLoss > 10.0) redFlags.push('High Packet Loss (>10%)');
                if (avgJitter > 30.0) redFlags.push('High Jitter (>30ms)');
                if (hasFlush) redFlags.push('Micro-Outages Detected');
                if (hasBufferStress) redFlags.push('Buffer Stress (>120)');
                
                return redFlags.length > 0 ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Red Flags Detected:</Typography>
                    {redFlags.map((flag, idx) => (
                      <Chip key={idx} label={flag} size="small" color="warning" sx={{ mr: 1, mt: 1 }} />
                    ))}
                  </Alert>
                ) : null;
              })()
            )}
            
            {/* All Metrics Combined Chart */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Audio Quality Metrics Over Time
                </Typography>
                <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
                  <Tab label="R-Factor" />
                  <Tab label="MOS Score" />
                  <Tab label="Jitter" />
                  <Tab label="Packet Loss" />
                </Tabs>

                {tabValue === 0 && data?.r_factor && (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data.r_factor.map((item, idx) => ({
                      ...item,
                      timestamp: new Date(item.timestamp).toLocaleTimeString(),
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="timestamp" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="value" name="R-Factor" stroke="#9c27b0" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                )}

                {tabValue === 1 && data?.mos && (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data.mos.map((item, idx) => ({
                      ...item,
                      timestamp: new Date(item.timestamp).toLocaleTimeString(),
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="timestamp" />
                      <YAxis domain={[0, 5]} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="value" name="MOS" stroke="#2196f3" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                )}

                {tabValue === 2 && data?.jitter && (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data.jitter.map((item, idx) => ({
                      ...item,
                      timestamp: new Date(item.timestamp).toLocaleTimeString(),
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="timestamp" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="value" name="Jitter (ms)" stroke="#ff9800" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                )}

                {tabValue === 3 && data?.packet_loss && (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data.packet_loss.map((item, idx) => ({
                      ...item,
                      timestamp: new Date(item.timestamp).toLocaleTimeString(),
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="timestamp" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="value" name="Packet Loss %" stroke="#f44336" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Summary Stats Grid */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">Avg R-Factor</Typography>
                    <Typography variant="h5" sx={{ color: getRFactorColor(data?.r_factor?.reduce((sum, d) => sum + d.value, 0) / (data?.r_factor?.length || 1)) }}>
                      {data?.r_factor?.length > 0 ? (data.r_factor.reduce((sum, d) => sum + d.value, 0) / data.r_factor.length).toFixed(1) : 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {data?.r_factor?.length > 0 ? getRFactorLabel(data.r_factor.reduce((sum, d) => sum + d.value, 0) / data.r_factor.length) : ''}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">Avg MOS</Typography>
                    <MuiTooltip title={getMOSQualityText(data?.mos?.reduce((sum, d) => sum + d.value, 0) / (data?.mos?.length || 1))} arrow>
                      <Typography variant="h5" sx={{ color: getMOSColor(data?.mos?.reduce((sum, d) => sum + d.value, 0) / (data?.mos?.length || 1)), cursor: 'help' }}>
                        {data?.mos?.length > 0 ? (data.mos.reduce((sum, d) => sum + d.value, 0) / data.mos.length).toFixed(2) : 'N/A'}
                      </Typography>
                    </MuiTooltip>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">Avg Jitter (ms)</Typography>
                    <Typography variant="h5">
                      {data?.jitter?.length > 0 ? (data.jitter.reduce((sum, d) => sum + d.value, 0) / data.jitter.length).toFixed(2) : 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">Avg Packet Loss %</Typography>
                    <Typography variant="h5" sx={{ color: (data?.packet_loss?.reduce((sum, d) => sum + d.value, 0) / (data?.packet_loss?.length || 1)) > 1 ? '#f44336' : '#4caf50' }}>
                      {data?.packet_loss?.length > 0 ? (data.packet_loss.reduce((sum, d) => sum + d.value, 0) / data.packet_loss.length).toFixed(2) : 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">Total Calls</Typography>
                    <Typography variant="h5">
                      {data?.total_calls || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Calls List Table */}
            <Box sx={{ mt: 3, borderTop: 1, borderColor: 'divider', pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Recent Calls
              </Typography>
              <CallsListTable cluster={cluster} agentId={agentId} hours={hours} />
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Agent Table Component (Best/Worst performers)
function AgentPerformanceTable({ agents, title, icon, onAgentClick }) {
  if (!agents || agents.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            {icon} {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">No data</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, px: 1, pt: 1 }}>
          {icon} {title}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>Agent</TableCell>
                <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>R-Factor</TableCell>
                <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>MOS</TableCell>
                <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>Loss%</TableCell>
                <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>Calls</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.map((agent, idx) => (
                <TableRow 
                  key={idx} 
                  hover
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => onAgentClick(agent.agent_id)}
                >
                  <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>{agent.agent_id}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                    <Chip 
                      label={agent.avg_r_factor || 'N/A'}
                      size="small"
                      sx={{ 
                        bgcolor: getRFactorColor(agent.avg_r_factor),
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        height: 20
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                    <MuiTooltip title={getMOSQualityText(agent.avg_mos)} arrow>
                      <Chip 
                        label={agent.avg_mos}
                        size="small"
                        sx={{ 
                          bgcolor: getMOSColor(agent.avg_mos),
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          height: 20,
                          cursor: 'help'
                        }}
                      />
                    </MuiTooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>{agent.avg_packet_loss}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>{agent.call_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

// Cluster Accordion - Dense format
function ClusterAccordion({ cluster, clusterData, onAgentClick }) {
  const best = clusterData.best_performers || [];
  const worst = clusterData.worst_performers || [];
  const total = clusterData.total_agents || 0;
  const avgMos = clusterData.avg_mos || 0;
  const avgRFactor = clusterData.avg_r_factor || 0;
  const totalCalls = clusterData.total_calls || 0;
  const acd = clusterData.acd || 0;
  const asrInbound = clusterData.asr_inbound || 0;
  const asrOutbound = clusterData.asr_outbound || 0;
  const inboundCalls = clusterData.inbound_calls || 0;
  const outboundCalls = clusterData.outbound_calls || 0;

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2, alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', minWidth: '100px' }}>
            {cluster.toUpperCase()}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip 
              label={`${total} agents`} 
              color="primary" 
              size="small"
            />
            <Chip 
              label={`Calls: ${totalCalls}`} 
              size="small"
              variant="outlined"
            />
            <Chip 
              label={`ACD: ${acd}s`} 
              size="small"
              variant="outlined"
            />
            <Chip 
              label={`ASR In: ${asrInbound}%`} 
              size="small"
              variant="outlined"
              sx={{ bgcolor: inboundCalls === 0 ? 'grey.500' : asrInbound >= 80 ? 'success.light' : asrInbound >= 60 ? 'warning.light' : 'error.light', color: 'white' }}
            />
            <Chip 
              label={`ASR Out: ${asrOutbound}%`} 
              size="small"
              variant="outlined"
              sx={{ bgcolor: outboundCalls === 0 ? 'grey.500' : asrOutbound >= 80 ? 'success.light' : asrOutbound >= 60 ? 'warning.light' : 'error.light', color: 'white' }}
            />
            <MuiTooltip title={getMOSQualityText(avgMos)} arrow>
              <Chip 
                label={`MOS: ${avgMos.toFixed(2)}`} 
                size="small"
                sx={{ 
                  bgcolor: getMOSColor(avgMos),
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'help'
                }}
              />
            </MuiTooltip>
            <Chip 
              label={`R-Factor: ${avgRFactor > 0 ? avgRFactor.toFixed(1) : 'N/A'}`} 
              size="small"
              sx={{ 
                bgcolor: avgRFactor > 0 ? getRFactorColor(avgRFactor) : '#666',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <AgentPerformanceTable 
              agents={best}
              title="Top 5 Performers"
              icon={<TrendingUpIcon fontSize="small" sx={{ color: 'success.main' }} />}
              onAgentClick={(agentId) => onAgentClick(cluster, agentId)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AgentPerformanceTable 
              agents={worst}
              title="Bottom 5 Performers"
              icon={<TrendingDownIcon fontSize="small" sx={{ color: 'error.main' }} />}
              onAgentClick={(agentId) => onAgentClick(cluster, agentId)}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

function AudioQuality() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [selectedAgent, setSelectedAgent] = useState({ cluster: null, agentId: null });
  const [modalOpen, setModalOpen] = useState(false);
  const [timeRange, setTimeRange] = useState(24); // Time range in hours
  const [autoRefresh, setAutoRefresh] = useState(120000); // Auto-refresh interval in milliseconds (default: 2 minutes)

  // Time range options (in hours)
  const timeRangeOptions = [
    { label: '1 hour', value: 1 },
    { label: '4 hours', value: 4 },
    { label: '12 hours', value: 12 },
    { label: '24 hours', value: 24 },
    { label: '48 hours', value: 48 },
    { label: '7 days', value: 168 },
  ];

  // Auto-refresh options (in milliseconds, 0 for None)
  const autoRefreshOptions = [
    { label: '1 minute', value: 60000 },
    { label: '2 minutes', value: 120000 },
    { label: '5 minutes', value: 300000 },
    { label: '10 minutes', value: 600000 },
    { label: 'None', value: 0 },
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await monitoringAPI.get(`/dashboard/audio-quality/agents?hours=${timeRange}`);
      setData(response.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load audio quality data');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  // Fetch data immediately when time range changes
  useEffect(() => {
    fetchData();
  }, [timeRange]); // Direct dependency on timeRange for immediate refresh

  // Setup auto-refresh interval - pauses when modal is open
  useEffect(() => {
    if (autoRefresh === 0) return; // No auto-refresh
    if (modalOpen) return; // Pause refresh when viewing agent details
    
    const interval = setInterval(fetchData, autoRefresh);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData, modalOpen]);

  // Format time range label
  const getTimeRangeLabel = () => {
    const option = timeRangeOptions.find(opt => opt.value === timeRange);
    return option ? option.label : 'Unknown';
  };

  const handleAgentClick = (cluster, agentId) => {
    setSelectedAgent({ cluster, agentId });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedAgent({ cluster: null, agentId: null });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const clusters = data?.clusters || {};
  const clusterNames = Object.keys(clusters).sort();

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header with controls */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h5" component="h1">
              Agent Audio Quality by Cluster
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getTimeRangeLabel()} • {clusterNames.length} clusters • Click agent for details
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="time-range-label">Time Range</InputLabel>
              <Select
                labelId="time-range-label"
                id="time-range-select"
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                {timeRangeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="auto-refresh-label">Auto Refresh</InputLabel>
              <Select
                labelId="auto-refresh-label"
                id="auto-refresh-select"
                value={autoRefresh}
                label="Auto Refresh"
                onChange={(e) => setAutoRefresh(e.target.value)}
              >
                {autoRefreshOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Last updated: {lastUpdate.toLocaleTimeString()}
        </Typography>
      </Box>

      {clusterNames.length === 0 ? (
        <Alert severity="info">No agent audio quality data available for the selected time period.</Alert>
      ) : (
        <Stack spacing={0.5}>
          {clusterNames.map((clusterName) => (
            <ClusterAccordion 
              key={clusterName} 
              cluster={clusterName}
              clusterData={clusters[clusterName]}
              onAgentClick={handleAgentClick}
            />
          ))}
        </Stack>
      )}

      {/* Agent Detail Modal */}
      <AgentDetailModal 
        open={modalOpen}
        onClose={handleCloseModal}
        cluster={selectedAgent.cluster}
        agentId={selectedAgent.agentId}
        hours={timeRange}
      />
    </Container>
  );
}

export default AudioQuality;
